import { h, Host } from "@stencil/core";
import { SyncWithRAF } from "../../common/sync-with-frames";
import { cellsInViewportAreLoadedAndVisible } from "./cells-in-viewport-are-rendered-and-visible";
import { getNewStartAndEndIndexes } from "./get-new-start-and-end-indexes";
import { updateVirtualScrollSize } from "./update-virtual-scroll";
import { emptyItems } from "./utils";
const VIRTUAL_SCROLL_CUSTOM_VAR_PREFIX = "--ch-virtual-scroll__scroll-";
const VIRTUAL_SCROLL_START_SIZE_CUSTOM_VAR = `${VIRTUAL_SCROLL_CUSTOM_VAR_PREFIX}start-size`;
const VIRTUAL_SCROLL_START_DISPLAY_CUSTOM_VAR = `${VIRTUAL_SCROLL_CUSTOM_VAR_PREFIX}start-display`;
const VIRTUAL_SCROLL_END_SIZE_CUSTOM_VAR = `${VIRTUAL_SCROLL_CUSTOM_VAR_PREFIX}end-size`;
const VIRTUAL_SCROLL_END_DISPLAY_CUSTOM_VAR = `${VIRTUAL_SCROLL_CUSTOM_VAR_PREFIX}end-display`;
export class ChVirtualScroller {
    constructor() {
        this.waitingForContent = true;
        this.bufferAmount = 5;
        this.initialRenderViewportItems = 10;
        this.inverseLoading = false;
        this.items = undefined;
        this.itemsCount = undefined;
        this.mode = "virtual-scroll";
    }
    #startIndex = 0;
    #endIndex = 0;
    #virtualStartSize = 0;
    #virtualEndSize = 0;
    #canUpdateRenderedCells = true;
    #waitingForCellsToBeRendered = false;
    #abortController; // Allocated at runtime to save resources
    #syncWithRAF = new SyncWithRAF();
    #virtualSizes; // Allocated at runtime to save resources
    #resizeObserver;
    /**
     * This element represents the ch-smart-grid element.
     * ```tsx
     *   <ch-smart-grid>
     *     #shadow-root (open)
     *     |  <ch-infinite-scroll></ch-infinite-scroll>
     *     |  <slot name="grid-content"></slot>
     *     <ch-smart-grid-virtual-scroller slot="grid-content">
     *       <ch-smart-grid-cell>...</ch-smart-grid-cell>
     *       <ch-smart-grid-cell>...</ch-smart-grid-cell>
     *       ...
     *     </ch-smart-grid-virtual-scroller>
     *   </ch-smart-grid>
     * ```
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #smartGrid;
    el;
    waitingForContentChanged() {
        this.#setVirtualScroller();
    }
    itemsChanged(newItems) {
        this.#resetVirtualScrollerState();
        this.#setViewportItemsOnInitialRender(newItems);
    }
    /**
     * Emitted when the array of visible items in the ch-smart-grid changes.
     */
    virtualItemsChanged;
    /**
     * Fired when the visible content of the virtual scroller did render for the
     * first time.
     */
    virtualScrollerDidLoad;
    /**
     * Add items to the beginning or end of the items property. This method is
     * useful for adding new items to the collection, without impacting in the
     * internal indexes used to display the virtual items. Without this method,
     * the virtual scroll would behave unexpectedly when new items are added.
     */
    async addItems(position, ...items) {
        if (position === "start") {
            this.items.unshift(...items);
            const newItemsCount = items.length;
            this.#startIndex += newItemsCount;
            this.#endIndex += newItemsCount;
        }
        else {
            this.items.push(...items);
        }
        this.#handleSmartGridContentScroll();
    }
    #toggleVirtualScrollEndVisibility = () => {
        const makeVisible = this.#virtualEndSize !== 0;
        if (makeVisible) {
            this.el.style.setProperty(VIRTUAL_SCROLL_END_DISPLAY_CUSTOM_VAR, "block");
        }
        // The virtual size must be "destroyed" to avoid displaying and
        // unnecessary gap at the end of the scroll
        else {
            this.el.style.removeProperty(VIRTUAL_SCROLL_END_DISPLAY_CUSTOM_VAR);
        }
    };
    // TODO: Check what happens when the cells has margin
    #updateVirtualScrollSize = (removedCells) => {
        this.#virtualStartSize = 0;
        this.#virtualEndSize = 0;
        const items = this.items;
        const virtualSizes = this.#virtualSizes;
        const lastIndex = items.length - 1;
        // - - - - - - - - - - - - - DOM read operations - - - - - - - - - - - - -
        const lastIndexForVirtualStartSize = Math.max(0, this.#startIndex - 1);
        const firstIndexForVirtualEndSize = Math.min(lastIndex, this.#endIndex + 1);
        const lastCellForVirtualStartSize = virtualSizes.get(items[lastIndexForVirtualStartSize].id);
        const firstCellForVirtualEndSize = virtualSizes.get(items[firstIndexForVirtualEndSize].id);
        // The virtual start size is at least the last unrendered cell
        if (lastCellForVirtualStartSize) {
            this.#virtualStartSize =
                lastCellForVirtualStartSize.offsetTop +
                    lastCellForVirtualStartSize.height;
        }
        const additionalHeightsStartIndex = Math.max(1, this.#startIndex);
        // TODO: Add support for gap in this virtual sizes
        // Between two different animation frames, rendered cells can be destroyed
        // and replaced by other new ones. When cells are destroyed the scroll size
        // must be maintained. To do this, the virtual start size is increased by
        // the size of the cells that have not yet been rendered
        for (let virtualIndex = additionalHeightsStartIndex; virtualIndex < this.#endIndex; virtualIndex++) {
            const cellIdToAddVirtualSize = items[virtualIndex].id;
            const virtualSize = virtualSizes.get(cellIdToAddVirtualSize);
            if (virtualSize) {
                this.#virtualStartSize += virtualSize.height;
            }
        }
        // Additional size for the virtual start scroll
        if (this.#endIndex === lastIndex) {
            const cellIdToAddVirtualSize = items[this.#endIndex].id;
            const virtualSize = virtualSizes.get(cellIdToAddVirtualSize);
            if (virtualSize) {
                this.#virtualStartSize += virtualSize.height;
            }
        }
        // Virtual end scroll
        else if (firstCellForVirtualEndSize) {
            let maxSmartGridVirtualHeight = 0;
            virtualSizes.forEach(virtualSize => {
                maxSmartGridVirtualHeight = Math.max(maxSmartGridVirtualHeight, virtualSize.offsetTop + virtualSize.height);
            });
            this.#virtualEndSize =
                maxSmartGridVirtualHeight - firstCellForVirtualEndSize.offsetTop;
        }
        // - - - - - - - - - - - - - DOM write operations - - - - - - - - - - - - -
        // Faster removal of the cells, due to the virtual height will be updated
        // in the next DOM write operation
        removedCells?.forEach(removedCell => {
            removedCell.style.display = "none";
        });
        this.el.style.setProperty(VIRTUAL_SCROLL_START_SIZE_CUSTOM_VAR, `${this.#virtualStartSize}px`);
        this.el.style.setProperty(VIRTUAL_SCROLL_START_DISPLAY_CUSTOM_VAR, 
        // The virtual size must be "destroyed" to avoid displaying and
        // unnecessary gap at the start of the scroll
        this.#virtualStartSize === 0 ? "none" : "block");
        this.el.style.setProperty(VIRTUAL_SCROLL_END_SIZE_CUSTOM_VAR, `${this.#virtualEndSize}px`);
        this.#toggleVirtualScrollEndVisibility();
    };
    #handleSmartGridContentScroll = () => {
        if (this.#canUpdateRenderedCells) {
            this.#syncWithRAF.perform(this.#updateRenderedCells);
        }
    };
    #updateRenderedCells = () => {
        // this.#canUpdateRenderedCells = false;
        // requestAnimationFrame(() => {
        //   this.#canUpdateRenderedCells = true;
        // });
        const cellsToRender = getNewStartAndEndIndexes(this.el, this.#smartGrid, this.items, this.#virtualSizes, this.#virtualStartSize, this.#virtualEndSize, this.bufferAmount, this.inverseLoading);
        if (cellsToRender.type === "waiting-for-cells-to-render") {
            this.#waitingForCellsToBeRendered = true;
            return;
        }
        this.#waitingForCellsToBeRendered = false;
        if (cellsToRender.type === "shift") {
            if (cellsToRender.startShift === 0 && cellsToRender.endShift === 0) {
                return;
            }
            this.#shiftIndex(cellsToRender.startShift, cellsToRender.endShift, cellsToRender.renderedCells);
        }
        //
        else {
            const { startIndex, endIndex } = cellsToRender;
            // Nothing to update
            if (this.#startIndex === startIndex && this.#endIndex === endIndex) {
                return;
            }
            const removedCells = updateVirtualScrollSize(cellsToRender, this.#virtualSizes, this.items);
            this.#startIndex = startIndex;
            this.#endIndex = endIndex;
            this.#emitVirtualItemsChange(removedCells);
        }
    };
    #emitVirtualItemsChange = (removedCells) => {
        this.#updateVirtualScrollSize(removedCells);
        const virtualItems = this.items.slice(this.#startIndex, this.#endIndex + 1);
        this.virtualItemsChanged.emit({
            startIndex: this.#startIndex,
            endIndex: this.#endIndex,
            totalItems: this.items.length,
            virtualItems
        });
    };
    #resetVirtualScrollerState = () => {
        this.#virtualStartSize = 0;
        this.#virtualEndSize = 0;
        // Render the last items when the scroll is inverted
        if (this.inverseLoading) {
            const lastIndex = this.items.length - 1;
            this.#startIndex = lastIndex;
            this.#endIndex = lastIndex;
        }
        else {
            this.#startIndex = 0;
            this.#endIndex = 0;
        }
        if (this.mode === "virtual-scroll") {
            this.#virtualSizes = new Map();
        }
    };
    #setViewportItemsOnInitialRender = (items) => {
        if (emptyItems(items)) {
            this.#emitVirtualItemsChange();
            return;
        }
        const indexToShift = this.initialRenderViewportItems + this.bufferAmount;
        if (this.inverseLoading) {
            this.#shiftIndex(indexToShift, 0);
        }
        else {
            this.#shiftIndex(0, indexToShift);
        }
    };
    #shiftIndex = (startIncomingShift, endIncomingShift, renderedCells) => {
        const startShift = this.mode === "lazy-render"
            ? Math.max(0, startIncomingShift)
            : startIncomingShift;
        const endShift = this.mode === "lazy-render"
            ? Math.max(0, endIncomingShift)
            : endIncomingShift;
        // Nothing to update
        if (startShift === 0 && endShift === 0) {
            return;
        }
        const newEndIndex = Math.min(this.#endIndex + endShift, this.items.length - 1);
        // const lastEndIndex = this.items.length - 1 === endShift;
        // const adjustmentToAvoidFlickering = lastEndIndex ? 1 : 0;
        const newStartIndex = Math.max(0, this.#startIndex - startShift);
        // Nothing to update
        if (this.#startIndex === newStartIndex &&
            this.#endIndex === newEndIndex &&
            // TODO: Add a unit test for this use case
            this.items.length > 1) {
            return;
        }
        this.#startIndex = newStartIndex;
        this.#endIndex = newEndIndex;
        const removedCells = this.mode === "virtual-scroll" &&
            renderedCells !== undefined &&
            renderedCells.length > 0
            ? updateVirtualScrollSize({
                endIndex: this.#endIndex,
                startIndex: this.#startIndex,
                renderedCells,
                type: "index"
            }, this.#virtualSizes, this.items)
            : [];
        this.#emitVirtualItemsChange(removedCells);
    };
    #setVirtualScroller = () => {
        this.#abortController = new AbortController();
        // RAF is used to avoid unnecessary check on the initial load when using
        // inverseLoading, since on the initial load the scroll will be repositioned
        requestAnimationFrame(() => {
            this.#smartGrid.addEventListener("scroll", this.#handleSmartGridContentScroll, {
                passive: true,
                signal: this.#abortController.signal
            });
            this.#resizeObserver = new ResizeObserver(this.#handleSmartGridContentScroll);
            this.#resizeObserver.observe(this.#smartGrid);
        });
    };
    #handleRenderedCell = (event) => {
        if (this.waitingForContent) {
            this.#checkInitialRenderVisibility();
        }
        else {
            this.#checkCellsRenderedAtRuntime(event.detail);
        }
    };
    #checkInitialRenderVisibility = () => this.#syncWithRAF.perform(() => {
        const waitingForContent = !cellsInViewportAreLoadedAndVisible(this.el, this.#smartGrid, this.inverseLoading);
        if (!waitingForContent) {
            requestAnimationFrame(() => {
                this.virtualScrollerDidLoad.emit();
                this.waitingForContent = waitingForContent;
            });
        }
    });
    #checkCellsRenderedAtRuntime = (cellId) => {
        // Delete virtual size, since the cell is now rendered
        this.#virtualSizes.delete(cellId);
        this.#updateVirtualScrollSize();
        if (this.#waitingForCellsToBeRendered) {
            this.#handleSmartGridContentScroll();
        }
    };
    connectedCallback() {
        // Listen for the render of the smart cells
        this.el.addEventListener("smartCellDidLoad", this.#handleRenderedCell);
        this.#resetVirtualScrollerState();
    }
    componentDidLoad() {
        this.#smartGrid = this.el.closest("ch-smart-grid");
        this.#setViewportItemsOnInitialRender(this.items);
    }
    disconnectedCallback() {
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = undefined;
        this.#syncWithRAF.cancel();
        this.#syncWithRAF = undefined;
        // Remove scroll events in the smart grid
        this.#abortController.abort();
    }
    render() {
        return (h(Host, { key: '5bdad3c7d092ca33aba837b0b27339b7ea7190d7', class: {
                "ch-virtual-scroller--content-not-loaded": this.waitingForContent,
                "ch-virtual-scroller--content-loaded": !this.waitingForContent,
                "ch-virtual-scroller--virtual-scroll": !this.waitingForContent && this.mode === "virtual-scroll"
            } }, h("slot", { key: 'f3b2a56b3f4eb57b757eda9acadebdf743336a56', onSlotchange: this.waitingForContent
                ? this.#checkInitialRenderVisibility
                : undefined })));
    }
    static get is() { return "ch-virtual-scroller"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["virtual-scroller.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["virtual-scroller.css"]
        };
    }
    static get properties() {
        return {
            "bufferAmount": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The number of elements to be rendered above and below the current\ncontainer's viewport."
                },
                "attribute": "buffer-amount",
                "reflect": false,
                "defaultValue": "5"
            },
            "initialRenderViewportItems": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies an estimation for the items that will enter in the viewport of\nthe initial render."
                },
                "attribute": "initial-render-viewport-items",
                "reflect": false,
                "defaultValue": "10"
            },
            "inverseLoading": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "When set to `true`, the grid items will be loaded in inverse order, with\nthe scroll positioned at the bottom on the initial load.\n\nIf `mode=\"virtual-scroll\"`, only the items at the start of the viewport\nthat are not visible will be removed from the DOM. The items at the end of\nthe viewport that are not visible will remain rendered to avoid flickering\nissues."
                },
                "attribute": "inverse-loading",
                "reflect": false,
                "defaultValue": "false"
            },
            "items": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "SmartGridModel | undefined",
                    "resolved": "SmartGridItem[]",
                    "references": {
                        "SmartGridModel": {
                            "location": "import",
                            "path": "../smart-grid/types",
                            "id": "src/components/smart-grid/types.ts::SmartGridModel"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The array of items to be rendered in the ch-smart-grid."
                }
            },
            "itemsCount": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The number of elements in the items array.\nUse if the array changes, without recreating the array."
                },
                "attribute": "items-count",
                "reflect": false
            },
            "mode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"virtual-scroll\" | \"lazy-render\"",
                    "resolved": "\"lazy-render\" | \"virtual-scroll\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the control will behave."
                },
                "attribute": "mode",
                "reflect": false,
                "defaultValue": "\"virtual-scroll\""
            }
        };
    }
    static get states() {
        return {
            "waitingForContent": {}
        };
    }
    static get events() {
        return [{
                "method": "virtualItemsChanged",
                "name": "virtualItemsChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the array of visible items in the ch-smart-grid changes."
                },
                "complexType": {
                    "original": "VirtualScrollVirtualItems",
                    "resolved": "{ virtualItems: SmartGridModel; startIndex: number; endIndex: number; totalItems: number; }",
                    "references": {
                        "VirtualScrollVirtualItems": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/virtual-scroller/types.ts::VirtualScrollVirtualItems"
                        }
                    }
                }
            }, {
                "method": "virtualScrollerDidLoad",
                "name": "virtualScrollerDidLoad",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the visible content of the virtual scroller did render for the\nfirst time."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "addItems": {
                "complexType": {
                    "signature": "(position: \"start\" | \"end\", ...items: SmartGridModel) => Promise<void>",
                    "parameters": [{
                            "name": "position",
                            "type": "\"start\" | \"end\"",
                            "docs": ""
                        }, {
                            "name": "items",
                            "type": "SmartGridItem[]",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "SmartGridModel": {
                            "location": "import",
                            "path": "../smart-grid/types",
                            "id": "src/components/smart-grid/types.ts::SmartGridModel"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Add items to the beginning or end of the items property. This method is\nuseful for adding new items to the collection, without impacting in the\ninternal indexes used to display the virtual items. Without this method,\nthe virtual scroll would behave unexpectedly when new items are added.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "waitingForContent",
                "methodName": "waitingForContentChanged"
            }, {
                "propName": "items",
                "methodName": "itemsChanged"
            }];
    }
}
